import {
  LayoutDashboard, LogIn, CalendarCheck, LogOut, Receipt, ArrowLeftRight,
  Wrench, Users, ShoppingCart, FileText, Settings, KeyRound, BedDouble, Package, Clock, Cable, Mail, ClipboardList,
} from "lucide-react";

export const UI_LANGS = ["nl", "en", "es"];

// Volledige catalogus van menu-items met standaardgroep en capaciteitseisen.
export const MENU_CATALOG = [
  { key: "nav_dashboard", path: "/", icon: LayoutDashboard, group: "group_daily" },
  { key: "nav_reservaties", path: "/reservaties", icon: CalendarCheck, group: "group_daily" },
  { key: "nav_aankomend", path: "/aankomsten", icon: LogIn, group: "group_daily" },
  { key: "nav_verblijven", path: "/verblijven", icon: BedDouble, group: "group_daily" },
  { key: "nav_vertrekkend", path: "/vertrekkend", icon: LogOut, group: "group_daily" },
  { key: "nav_verhuizen", path: "/verhuizen", icon: ArrowLeftRight, group: "group_daily" },
  { key: "nav_gasten", path: "/gasten", icon: Users, group: "group_daily" },
  { key: "nav_mailbox", path: "/mailbox", icon: Mail, group: "group_daily", mailbox: true },
  { key: "nav_jobs", path: "/jobs", icon: ClipboardList, group: "group_daily" },
  { key: "nav_afrekening", path: "/afrekening", icon: Receipt, group: "group_billing", fin: 1 },
  { key: "nav_verkopen", path: "/verkopen", icon: ShoppingCart, group: "group_billing", fin: 1 },
  { key: "nav_facturen", path: "/facturen", icon: FileText, group: "group_billing", fin: 1 },
  { key: "nav_verhuur", path: "/verhuur", icon: Package, group: "group_manage" },
  { key: "nav_verbeteringen", path: "/verbeteringen", icon: Wrench, group: "group_manage", admin: true },
  { key: "my_hours", path: "/uren", icon: Clock, group: "group_manage" },
  { key: "nav_admin", path: "/admin", icon: Settings, group: "group_manage", admin: true },
  { key: "nav_wachtwoord", path: "/wachtwoord", icon: KeyRound, group: "group_manage" },
  { key: "nav_interfaces", path: "/interfaces", icon: Cable, group: "group_manage" },
];

export const CATALOG_BY_KEY = Object.fromEntries(MENU_CATALOG.map((i) => [i.key, i]));

// Rollen waarvoor het menu instelbaar is (superadmin ziet altijd alles).
export const CONFIGURABLE_ROLES = ["admin", "reception", "personeel", "controller"];

// Standaardgroepen met per-taal namen.
export const DEFAULT_GROUPS = [
  { id: "group_daily", labels: { nl: "Dagelijks", en: "Daily", es: "Diario" } },
  { id: "group_billing", labels: { nl: "Boekhouding", en: "Accounting", es: "Contabilidad" } },
  { id: "group_manage", labels: { nl: "Beheer", en: "Management", es: "Gestión" } },
];

export function groupLabel(group, lang) {
  const l = group?.labels || {};
  return (l[lang] || l.nl || l.en || l.es || "").trim() || group?.id || "";
}

// Standaard voor Reception: "Verhuur" onder "Losse verkopen" in de Boekhouding-groep.
const RECEPTION_ORDER = [
  "nav_dashboard", "nav_reservaties", "nav_aankomend", "nav_verblijven", "nav_vertrekkend", "nav_verhuizen", "nav_gasten", "nav_mailbox", "nav_jobs",
  "nav_afrekening", "nav_verkopen", "nav_verhuur", "nav_facturen",
  "nav_verbeteringen", "my_hours", "nav_admin", "nav_wachtwoord", "nav_interfaces",
];
const RECEPTION_GROUP = { nav_verhuur: "group_billing" };

export function defaultLayout(role) {
  const groups = DEFAULT_GROUPS.map((g) => ({ id: g.id, labels: { ...g.labels } }));
  let items;
  if (role === "reception") {
    items = RECEPTION_ORDER.map((key) => ({ key, group: RECEPTION_GROUP[key] || CATALOG_BY_KEY[key].group, visible: true }));
  } else {
    items = MENU_CATALOG.map((i) => ({ key: i.key, group: i.group, visible: true }));
  }
  return { groups, items };
}

// Voegt de opgeslagen config samen met de catalogus (nieuwe items blijven verschijnen,
// items van een verwijderde groep worden verborgen).
export function resolveLayout(role, config) {
  const base = defaultLayout(role);
  const saved = config?.roles?.[role];
  let groups, savedItems;
  if (Array.isArray(saved)) {
    groups = base.groups; savedItems = saved;
  } else if (saved && typeof saved === "object") {
    groups = Array.isArray(saved.groups) && saved.groups.length
      ? saved.groups.map((g) => ({ id: g.id, labels: { ...(g.labels || {}) } }))
      : base.groups;
    savedItems = Array.isArray(saved.items) ? saved.items : null;
  } else {
    return base;
  }
  if (!savedItems) return { groups, items: base.items };

  const groupIds = new Set(groups.map((g) => g.id));
  const firstGroup = groups[0]?.id || "group_daily";
  const seen = new Set();
  const items = [];
  for (const s of savedItems) {
    const cat = CATALOG_BY_KEY[s.key];
    if (!cat || seen.has(s.key)) continue;
    seen.add(s.key);
    const inGroup = groupIds.has(s.group);
    items.push({ key: s.key, group: inGroup ? s.group : firstGroup, visible: inGroup ? s.visible !== false : false });
  }
  for (const b of base.items) {
    if (seen.has(b.key)) continue;
    items.push({ key: b.key, group: groupIds.has(b.group) ? b.group : firstGroup, visible: true });
  }
  return { groups, items };
}

// Bouwt de gegroepeerde menustructuur uit een layout, met capaciteitsfilter (maySee).
export function buildMenu(layout, maySee, lang) {
  return layout.groups.map((g) => ({
    id: g.id,
    label: groupLabel(g, lang),
    items: layout.items
      .filter((i) => i.group === g.id && i.visible)
      .map((i) => CATALOG_BY_KEY[i.key])
      .filter((it) => it && maySee(it)),
  })).filter((g) => g.items.length);
}
